import dayjs, { Dayjs } from 'dayjs';
import { WeekScheduleDay, WeekScheduleDayInput } from 'graphql/generated/graphqlTypes';
import { FieldPathByValue } from 'react-hook-form';
import { DayName } from 'v2/hooks/useSchedules/useSchedules';

const CONSTANT_DATE = '2000-01-01';

export const TIME_PICKER_WIDTH = 120;

export const toHHMM = (time?: string) => (time ? dayjs(`${CONSTANT_DATE} ${time}`).format('HH:mm') : '');
export const toHHMMSS = (time: string) => dayjs(`${CONSTANT_DATE} ${time}`).format('HH:mm:ss');
export const responsiveRowSx = {
  '@container (max-width: 400px)': {
    flexDirection: 'column'
  }
} as const;

export type ScheduleMode = 'custom' | 'same_every_workday' | 'same_every_day';

export type TimeFieldName = FieldPathByValue<EditScheduleFormValues, string>;

export interface TimeRange {
  from: string;
  to: string;
}

export interface DayValues {
  enabled: boolean;
  from: string;
  to: string;
}

export interface EditScheduleFormValues {
  mode: ScheduleMode;
  allDays: TimeRange;
  workdays: TimeRange;
  monday: DayValues;
  tuesday: DayValues;
  wednesday: DayValues;
  thursday: DayValues;
  friday: DayValues;
  saturday: DayValues;
  sunday: DayValues;
}

export const WORKDAY_NAMES: DayName[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
export const ALL_DAY_NAMES: DayName[] = [...WORKDAY_NAMES, 'saturday', 'sunday'];

export const DAY_TIME_FIELDS: Record<
  DayName,
  { from: FieldPathByValue<EditScheduleFormValues, string>; to: FieldPathByValue<EditScheduleFormValues, string> }
> = {
  monday: { from: 'monday.from', to: 'monday.to' },
  tuesday: { from: 'tuesday.from', to: 'tuesday.to' },
  wednesday: { from: 'wednesday.from', to: 'wednesday.to' },
  thursday: { from: 'thursday.from', to: 'thursday.to' },
  friday: { from: 'friday.from', to: 'friday.to' },
  saturday: { from: 'saturday.from', to: 'saturday.to' },
  sunday: { from: 'sunday.from', to: 'sunday.to' }
};

export const getInitialMode = (weekDays: WeekScheduleDay[]): ScheduleMode => {
  if (weekDays.length === 0) return 'custom';

  if (weekDays.length === 7 && weekDays.every((d) => d.from === weekDays[0].from && d.to === weekDays[0].to))
    return 'same_every_day';

  const workdayEntries = weekDays.filter((d) => (WORKDAY_NAMES as string[]).includes(d.dayName));
  if (
    workdayEntries.length === 5 &&
    workdayEntries.every((d) => d.from === workdayEntries[0].from && d.to === workdayEntries[0].to)
  )
    return 'same_every_workday';

  return 'custom';
};

export const toTimeValue = (value: string): Dayjs | null => (value ? dayjs(`2000-01-01 ${value}`) : null);

export const getFormDefaults = (weekDays: WeekScheduleDay[]): EditScheduleFormValues => {
  const mode = getInitialMode(weekDays);
  const fallbackFrom = toHHMM(weekDays[0]?.from) || '07:00';
  const fallbackTo = toHHMM(weekDays[0]?.to) || '22:00';

  const getDayValues = (dayName: string): DayValues => {
    const day = weekDays.find((d) => d.dayName === dayName);
    if (day) return { enabled: true, from: toHHMM(day.from), to: toHHMM(day.to) };
    return { enabled: false, from: fallbackFrom, to: fallbackTo };
  };

  const firstWorkday = weekDays.find((d) => (WORKDAY_NAMES as string[]).includes(d.dayName));

  return {
    mode,
    allDays: { from: fallbackFrom, to: fallbackTo },
    workdays: {
      from: toHHMM(firstWorkday?.from) || fallbackFrom,
      to: toHHMM(firstWorkday?.to) || fallbackTo
    },
    monday: getDayValues('monday'),
    tuesday: getDayValues('tuesday'),
    wednesday: getDayValues('wednesday'),
    thursday: getDayValues('thursday'),
    friday: getDayValues('friday'),
    saturday: getDayValues('saturday'),
    sunday: getDayValues('sunday')
  };
};

export const buildWeekSchedule = (data: EditScheduleFormValues): WeekScheduleDayInput[] => {
  switch (data.mode) {
    case 'same_every_day':
      return ALL_DAY_NAMES.map((dayName) => ({
        dayName,
        from: toHHMMSS(data.allDays.from),
        to: toHHMMSS(data.allDays.to)
      }));

    case 'same_every_workday': {
      const schedule: WeekScheduleDayInput[] = WORKDAY_NAMES.map((dayName) => ({
        dayName,
        from: toHHMMSS(data.workdays.from),
        to: toHHMMSS(data.workdays.to)
      }));
      if (data.saturday.enabled)
        schedule.push({ dayName: 'saturday', from: toHHMMSS(data.saturday.from), to: toHHMMSS(data.saturday.to) });
      if (data.sunday.enabled)
        schedule.push({ dayName: 'sunday', from: toHHMMSS(data.sunday.from), to: toHHMMSS(data.sunday.to) });
      return schedule;
    }

    case 'custom':
      return ALL_DAY_NAMES.filter((dayName) => data[dayName].enabled).map((dayName) => ({
        dayName,
        from: toHHMMSS(data[dayName].from),
        to: toHHMMSS(data[dayName].to)
      }));
  }
};
